import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckCircle, XCircle, Minus, Activity } from "lucide-react";

function getRatingColor(rating: string) {
  switch (rating) {
    case "非常适合": return "text-emerald-400 border-emerald-500/40 bg-emerald-500/10";
    case "适合": return "text-green-400 border-green-500/40 bg-green-500/10";
    case "一般": return "text-yellow-400 border-yellow-500/40 bg-yellow-500/10";
    case "不适合": return "text-orange-400 border-orange-500/40 bg-orange-500/10";
    case "强烈不建议": return "text-red-400 border-red-500/40 bg-red-500/10";
    default: return "text-muted-foreground border-border bg-muted/10";
  }
}

function getRatingIcon(rating: string) {
  switch (rating) {
    case "非常适合": return <CheckCircle className="h-3 w-3 text-emerald-400" />;
    case "适合": return <CheckCircle className="h-3 w-3 text-green-400" />;
    case "一般": return <Minus className="h-3 w-3 text-yellow-400" />;
    case "不适合": return <XCircle className="h-3 w-3 text-orange-400" />;
    case "强烈不建议": return <XCircle className="h-3 w-3 text-red-400" />;
    default: return <Minus className="h-3 w-3" />;
  }
}

interface Props {
  stockCode: string;
  monitorId?: number;
  compact?: boolean;
}

export function StockSuitabilityBadge({ stockCode, monitorId, compact = true }: Props) {
  const { data, isLoading } = trpc.screening.stockSuitability.useQuery(
    { stockCode, monitorId },
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, refetchInterval: false, enabled: !!stockCode }
  );

  if (isLoading) return <span className="text-[10px] text-muted-foreground animate-pulse">分析中...</span>;
  if (!data) return null;

  const { score, rating, amplitude, turnoverRate, pctChange, winRate, totalRounds, factors, mktScore } = data;

  if (compact) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className={"text-[10px] px-1.5 py-0 h-5 cursor-help flex items-center gap-1 " + getRatingColor(rating)}>
              {getRatingIcon(rating)}
              <span>T0 {score}分</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="w-64 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">今日T0适合度</span>
              <span className={"text-xs font-bold " + getRatingColor(rating).split(' ')[0]}>{rating}</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
              <div className="flex justify-between"><span className="text-muted-foreground">振幅</span><span className="font-mono">{amplitude.toFixed(1)}%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">换手率</span><span className="font-mono">{turnoverRate.toFixed(1)}%</span></div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">涨跌幅</span>
                <span className={"font-mono " + (pctChange > 0 ? 'text-red-400' : pctChange < 0 ? 'text-green-400' : '')}>{pctChange > 0 ? '+' : ''}{pctChange.toFixed(2)}%</span>
              </div>
              {winRate !== null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">近30日胜率</span>
                  <span className={"font-mono " + (winRate >= 55 ? 'text-green-400' : winRate < 40 ? 'text-red-400' : '')}>{winRate}%({totalRounds}轮)</span>
                </div>
              )}
              {mktScore !== null && (
                <div className="flex justify-between col-span-2"><span className="text-muted-foreground">大盘环境</span><span className="font-mono">{mktScore}分</span></div>
              )}
            </div>
            {factors.length > 0 && (
              <div className="border-t border-border/30 pt-2 space-y-1">
                {factors.map((f: any, i: number) => (
                  <div key={i} className="flex items-start gap-1.5 text-[10px]">
                    <span className={"font-mono flex-shrink-0 " + (f.delta > 0 ? 'text-green-400' : f.delta < 0 ? 'text-red-400' : 'text-muted-foreground')}>
                      {f.delta > 0 ? '+' + f.delta : f.delta === 0 ? '+-0' : f.delta}
                    </span>
                    <span className="text-muted-foreground leading-tight">{f.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className={"rounded-lg border p-3 space-y-2 " + getRatingColor(rating).split(' ').slice(1).join(' ')}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">今日T0适合度</span>
        </div>
        <div className="flex items-center gap-1.5">
          {getRatingIcon(rating)}
          <span className={"text-sm font-bold " + getRatingColor(rating).split(' ')[0]}>{rating}</span>
          <span className="text-xs text-muted-foreground">({score}分)</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex justify-between"><span className="text-muted-foreground">今日振幅</span><span className="font-mono">{amplitude.toFixed(1)}%</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">换手率</span><span className="font-mono">{turnoverRate.toFixed(1)}%</span></div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">涨跌幅</span>
          <span className={"font-mono " + (pctChange > 0 ? 'text-red-400' : pctChange < 0 ? 'text-green-400' : '')}>{pctChange > 0 ? '+' : ''}{pctChange.toFixed(2)}%</span>
        </div>
        {winRate !== null && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">近30日胜率</span>
            <span className={"font-mono " + (winRate >= 55 ? 'text-green-400' : winRate < 40 ? 'text-red-400' : '')}>{winRate}%（{totalRounds}轮）</span>
          </div>
        )}
      </div>
      {factors.length > 0 && (
        <div className="border-t border-border/30 pt-2 space-y-1">
          {factors.map((f: any, i: number) => (
            <div key={i} className="flex items-start gap-2 text-[11px]">
              <span className={"font-mono w-8 flex-shrink-0 text-right " + (f.delta > 0 ? 'text-green-400' : f.delta < 0 ? 'text-red-400' : 'text-muted-foreground')}>
                {f.delta > 0 ? '+' + f.delta : f.delta === 0 ? '+-0' : f.delta}
              </span>
              <span className="text-muted-foreground leading-tight">{f.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
